/**
 * `/studio/clients/$slug` — `mockups/studio/client-detail.html`.
 *
 * One read (`clients_get_detail`) answers the whole page, and the page states what it
 * was given and computes nothing. `readmodels/studio-client-detail.ts` owns the
 * arithmetic — the month total, the next-invoice estimate, the effective rate, the
 * extras ledger, the scope changes folded into their parents — which is DESIGN.md P2
 * applied across the seam rather than within a page.
 *
 * ## `?section=` is a tab, and five of the six are the mockup's five
 *
 * `.docs/20` §C: "Client detail (`/studio/clients/$slug`, **tabs by `?section=`**)".
 * The enum the route validates with is `overview | retainer | work | money | widgets |
 * files | people`, which is the mockup's own section list in its own order with
 * **`files`** lifted out of the People region into a tab of its own (session 24;
 * `SECTIONS` below and `FilesSection.tsx` say why) and **`widgets`** added by P5 for a
 * control that had no screen at all (`WidgetsSection.tsx`). So a tab is one section of
 * the printed page, and two of the seven are sections the printed page never had. Two
 * consequences worth knowing:
 *
 *   - the hero lives on `overview` alone, so "one `.hero-figure` per page"
 *     (CONTRACT.md §4.6) holds on every tab rather than only on the first screenful;
 *   - the tab is in the URL, so "look at their retainer" is a link somebody can send.
 *
 * `?period=` is the topbar seg and is the month every figure on the page is read for.
 *
 * ## The account is not the retainer, and the topbar is where that matters
 *
 * `StudioClientDetailSchema.account` carries the client's own `name`, `tier`,
 * `currency` and `archived_at`, and every one of them is a fact about the **account**
 * rather than about a package it happens to be on. Three consequences, and each was
 * a control behaving wrongly until this page read the field it already had:
 *
 *   - the title is the **company** rather than the slug;
 *   - "New work order" is priced at the client's own tier and billed in the client's
 *     own currency — `account.currency` is "what they are billed in, whatever a
 *     retainer says" — so a prospect and a project-only client can be quoted.
 *     Reading either off the live retainer made both of them `null` and disabled the
 *     button for exactly the client worth quoting;
 *   - `archived_at` is what `.docs/20` §Edge cases' "no Impersonate for archived" is
 *     read from, and it is **not** "no live retainer". They are two facts: a prospect
 *     has no retainer and is not archived, an archived account may still hold a
 *     retainer row, and so they get two different banners below.
 *
 * The three are `null` together and only while the read is in flight, which is the
 * one thing `detail === null` means here.
 */

import {
  CurrencySchema,
  PeriodSchema,
  TierCodeSchema,
  parsePeriod,
  periodOf,
  type Currency,
  type TierCode,
} from '@fareast/shared'
import { z } from 'zod'

import { useStudioRead } from '../../../api/studio.js'
import { PeriodSeg } from '../../../components/client/index.js'
import { Seg, SegItem } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Row } from '../../../components/portal/Layout.js'
import { Notice } from '../../../components/portal/Status.js'
import { ImpersonateButton } from '../../../components/studio/ImpersonateButton.js'
import { useFormat } from '../../../format/useFormat.js'
import { now } from '../../../integrations/clock.js'
import { useSearchState } from '../../../search/useSearchState.js'
import { PageTitle } from '../../../shell/PageTitle.js'
import { HeroSection } from './HeroSection.js'
import { MoneySection } from './MoneySection.js'
import { NewWorkOrderButton } from './NewWorkOrderButton.js'
import { OpenItemsSection } from './OpenItemsSection.js'
import { FilesSection } from './FilesSection.js'
import { MockupsSection } from './MockupsSection.js'
import { PeopleSection } from './PeopleSection.js'
import { RetainerSection } from './RetainerSection.js'
import { WidgetsSection } from './WidgetsSection.js'
import { WorkOrdersSection } from './WorkOrdersSection.js'

/** The tabs and the month. The mockup's sections, as one key. */
export const ClientDetailSearchSchema = z.object({
  section: z
    .enum(['overview', 'retainer', 'work', 'money', 'widgets', 'files', 'mockups', 'people'])
    .default('overview'),
  period: PeriodSchema.optional(),
})

export type ClientDetailSection = z.infer<typeof ClientDetailSearchSchema>['section']

/**
 * The mockup's sections, in the order it prints them, plus one.
 *
 * **Files is a tab of its own from session 24.** The mockup draws it as the middle
 * column of the People/files/history region — six lines and four numbers, faithfully
 * rendered and entirely read-only — while `files_create_upload`, `files_release!`,
 * `files_hold!`, `files_archive!` and `files_request_review` had no screen anywhere in
 * the studio (R2-17). A per-file list with a control on every row does not fit in one
 * third of a `RegionCols`, and files belong to a **client**, so the tab is here rather
 * than on a work order (which could reach four of this client's two hundred files).
 * `FilesSection.tsx`'s header is the long version.
 *
 * The People tab keeps people and history and no longer prints the files column: one
 * fact, one place.
 */
/**
 * The tabs, in order. Exported because the **route** derives an address alias from
 * each label (`routes/studio/clients/$slug.tsx`): `?section=invoices` is Invoices,
 * not a typo to be swallowed into the Overview.
 */
export const SECTIONS: readonly { readonly key: ClientDetailSection; readonly label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'retainer', label: 'Retainer' },
  { key: 'work', label: 'Work orders' },
  { key: 'money', label: 'Invoices' },
  /*
   * **Widgets is a tab of its own from P5**, beside Invoices rather than under
   * People, and for the same reason Files became one: `widgets_set_config!` had no
   * screen anywhere in the studio (`WidgetsSection.tsx` is the long version). It sits
   * next to Invoices because a widget is a thing that meters and bills, and a studio
   * member looking at one is usually looking at what it costs.
   */
  { key: 'widgets', label: 'Widgets' },
  { key: 'files', label: 'Files' },
  // Session 48: the HTML designs shared with this client for feedback (`MockupsSection.tsx`).
  { key: 'mockups', label: 'Mockups' },
  { key: 'people', label: 'People' },
]

export type ClientDetailPageProps = { readonly slug: string }

export function ClientDetailPage({ slug }: ClientDetailPageProps) {
  const [search, setSearch] = useSearchState(ClientDetailSearchSchema)
  const format = useFormat()
  const latest = periodOf(now())
  const period = search.period ?? latest

  const read = useStudioRead('clients_get_detail', { client: slug, period })
  const detail = read.data ?? null
  const retainer = detail?.retainer ?? null
  const account = detail?.account ?? null

  /*
   * The tier and the currency the price builders are given, from the **account**.
   *
   * Parsed rather than passed through: the RPC types both as plain `number` and
   * `string`, and `catalog_compute_price` takes the closed unions. A tier nobody
   * published would otherwise reach the engine as a number and come back a 400 on a
   * quote somebody was halfway through.
   *
   * `null` here means "the read has not answered", and nothing else —
   * `StudioClientAccountSchema` makes both fields non-nullable for every client.
   */
  const tier: TierCode | null = account === null ? null : TierCodeSchema.parse(account.tier)
  const currency: Currency | null = account === null ? null : CurrencySchema.parse(account.currency)

  /** A closed account: read-only, nothing to quote, and nobody to view it as. */
  const archived = account !== null && account.archived_at !== null

  /*
   * **The company, not the slug.** `studio/client-detail.html` heads the page
   * "Casaturca" and `casaturca` is a URL segment; `StudioClientDetailSchema.account`
   * carries the organisation's own name for exactly this. The slug is the fallback
   * for the one render before the read answers, where there is nothing else to say.
   */
  const name = detail?.account.name ?? slug

  const title = (
    <PageTitle
      eyebrow={`Client · ${format.monthLong(period)}`}
      title={name}
      sub={
        retainer === null
          ? undefined
          : `${retainer.name} · Tier ${format.number(retainer.market_tier)} · ${retainer.currency}`
      }
      actions={
        <>
          <PeriodSeg
            value={period}
            latest={latest}
            onChange={(next) => {
              setSearch({ period: parsePeriod(next) })
            }}
          />
          {/*
            Both buttons are the account's, so both wait for it — the period seg
            above them does not, because a month is in the URL rather than in the
            answer. `tier` and `currency` are null exactly when `detail` is.
          */}
          {detail === null || tier === null || currency === null ? null : (
            <>
              <ImpersonateButton
                client={slug}
                members={detail.contacts.members}
                archived={archived}
              />
              <NewWorkOrderButton
                client={slug}
                tier={tier}
                currency={currency}
                retainerClient={retainer !== null}
                archived={archived}
              />
            </>
          )}
        </>
      }
    />
  )

  /* One muted line while it loads, and one if it never answers (DESIGN.md P7). */
  if (read.isPending) {
    return (
      <>
        {title}
        <Empty>Loading {slug}…</Empty>
      </>
    )
  }

  if (detail === null || tier === null || currency === null) {
    return (
      <>
        {title}
        <Empty>We could not load this client. Try again in a moment.</Empty>
      </>
    )
  }

  return (
    <>
      {title}

      <Row>
        <Seg label="Section">
          {SECTIONS.map((section) => (
            <SegItem
              key={section.key}
              on={section.key === search.section}
              onClick={() => {
                setSearch({ section: section.key })
              }}
            >
              {section.label}
            </SegItem>
          ))}
        </Seg>
      </Row>

      {/*
        Two banners, because they are two facts and the page used to print one of
        them for both. `.docs/20` §Edge cases: "Client detail for a cancelled client:
        read-only banner".

        `archived_at` is the closed account — read-only, nothing to quote, nobody to
        view it as — and it is stated in the routine strip rather than the ochre one
        (DESIGN.md P4: ochre is a decision or a cost, and a closed account is
        neither). A null **retainer** is a different thing entirely: `getForClient`
        reads the *live* one and `cancelled` is not live (`CLAUDE.md` §Gotchas), so it
        is what a prospect, a project-only client and a client who left all look like
        — none of which is a problem, and all of which can still be quoted.
      */}
      {archived ? (
        <Notice role="status">
          This account is archived. The invoices, the files and the history stay as the record;
          nothing new can be quoted, and there is nobody to view the portal as.
        </Notice>
      ) : retainer === null ? (
        <Notice role="status">
          No live retainer. The invoices, the files and the history stay, and a project can still be
          quoted; there is no package to edit until one is started.
        </Notice>
      ) : null}

      {search.section === 'overview' ? (
        <>
          <HeroSection detail={detail} />
          <OpenItemsSection detail={detail} />
        </>
      ) : null}

      {search.section === 'retainer' ? (
        <RetainerSection detail={detail} tier={tier} currency={currency} />
      ) : null}

      {search.section === 'work' ? <WorkOrdersSection detail={detail} /> : null}
      {search.section === 'money' ? <MoneySection detail={detail} /> : null}
      {search.section === 'widgets' ? <WidgetsSection slug={slug} detail={detail} /> : null}
      {search.section === 'files' ? <FilesSection slug={slug} detail={detail} /> : null}
      {search.section === 'mockups' ? <MockupsSection slug={slug} /> : null}
      {search.section === 'people' ? <PeopleSection detail={detail} /> : null}
    </>
  )
}
